import { type TipoDomandaDTO } from '@framework/api-client/api/inquiries/schemas/tipoDomandaDTO.ts';
import { useGetFrontRichiesteIdIntegrate } from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { EnumRichiestaDTOStato } from '@framework/api-client/api/inquiries-compile/schemas/enumRichiestaDTOStato.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { SubmissionLogic } from '@framework/frontoffice-submission/submission/SubmissionLogic.ts';

/**
 * Ottiene lo stato di integrazione di una richiesta, se è possibile integrare e
 * se ci sono integrazioni.
 */
export function useSubmissionsIntegrationsState(
  inquiry: TipoDomandaDTO | null | undefined,
  submission: RichiestaDTO | null | undefined,
) {
  const integrationsResult = useGetFrontRichiesteIdIntegrate(
    submission?.id ?? '',
    { query: { enabled: submission?.id != null } },
  );

  // Scarico le integrazioni per limitare FE la possibilità di crearne di nuove.
  // Infatti, se ci sono integrazioni in bozza, non è possibile crearne altre.
  // Questa informazione non è codificata nel BE, quindi la calcoliamo qui.
  const hasIntegrationDrafts =
    // biome-ignore lint/complexity/useOptionalChain: tipo "boolean", non "boolean | undefined"
    integrationsResult.data != null &&
    integrationsResult.data?.some(
      submission => submission.stato === EnumRichiestaDTOStato.BOZZA,
    ) &&
    integrationsResult.data?.length > 1;

  const draftIntegrationId = integrationsResult.data?.find(
    submission => submission.stato === EnumRichiestaDTOStato.BOZZA,
  )?.id;

  // Controlliamo se è possibile integrare la richiesta
  const canIntegrate =
    SubmissionLogic.canIntegrate(inquiry, submission) && !hasIntegrationDrafts;

  const isIntegrated = submission?.stato === EnumRichiestaDTOStato.INTEGRATA;

  const hasIntegrations = SubmissionLogic.hasIntegrations(submission);

  // Mostriamo la tab delle integrazioni solo se ci sono integrazioni.
  // Valutiamo anche il caso in cui ci sono, ma in bozza.
  const shouldShowIntegrationsTab = hasIntegrations || hasIntegrationDrafts;

  return {
    isIntegrated,
    canIntegrate,
    hasIntegrations,
    integrationsResult,
    hasIntegrationDrafts,
    shouldShowIntegrationsTab,
    draftIntegrationId,
  };
}
